import React, { useState, useEffect } from "react";
import { BsCalendar2WeekFill, BsFillCalendarPlusFill } from "react-icons/bs";
import { Button, Alert, Container, Row, Col, Form, Spinner, Badge } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";

const API_URL = "https://backend.ems.uk.hiradentallab.com/api/payroll";

export default function EditPayroll() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [payrollData, setPayrollData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    bonus: 0,
    otherDeductions: 0,
    deductionNotes: "",
  });

  /* ---------- Load payroll by ID ---------- */
  useEffect(() => {
    const fetchPayroll = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const res = await fetch(`${API_URL}/${id}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Failed to load payroll");

        setPayrollData(data.data);
        setFormData({
          bonus: data.data.bonus || 0,
          otherDeductions: data.data.otherDeductions || 0,
          deductionNotes: data.data.deductionNotes || "",
        });
      } catch (err) {
        console.error("Error loading payroll:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) fetchPayroll();
  }, [id]);

  /* ---------- Handlers ---------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "deductionNotes" ? value : Number(value) || 0,
    }));
  };

  const handleSubmit = async () => {
    setError(null);
    setShowAlert(false);
    setIsSaving(true);
    try {
      const res = await fetch(`${API_URL}/${id}/adjust`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bonus: Number(formData.bonus) || 0,
          otherDeductions: Number(formData.otherDeductions) || 0,
          deductionNotes: formData.deductionNotes,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update payroll");

      setPayrollData(data.data);
      setShowAlert(true);
      setTimeout(() => navigate("/emp-payroll"), 1500);
    } catch (err) {
      console.error("Error updating payroll:", err);
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setError(null);
    setIsSaving(true);
    try {
      const res = await fetch(`${API_URL}/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update status");
      setPayrollData(data.data);
      setShowAlert(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const fmt = (v, d = 2) => (Number(v) || 0).toFixed(d);
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "N/A");

  const statusVariant = {
    draft: "secondary",
    finalized: "warning",
    paid: "success",
  };

  /* ---------- Render ---------- */
  if (isLoading) {
    return (
      <Container fluid>
        <h1 className="holiday-title">
          <BsCalendar2WeekFill style={{ marginRight: "5px" }} />
          Public Holidays
        </h1>
        <hr />
        <h1 className="holiday-subtitle">
          <BsFillCalendarPlusFill style={{ marginRight: "5px" }} />
          Edit Payroll
        </h1>
        <hr />
        <div className="text-center mt-5">
          <Spinner animation="border" /> Loading payroll data...
        </div>
      </Container>
    );
  }

  if (error && !payrollData) {
    return (
      <Container fluid>
        <h1 className="holiday-title">
          <BsCalendar2WeekFill style={{ marginRight: "5px" }} />
          Public Holidays
        </h1>
        <hr />
        <h1 className="holiday-subtitle">
          <BsFillCalendarPlusFill style={{ marginRight: "5px" }} />
          Edit Payroll
        </h1>
        <hr />
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container fluid>
      <h1 className="holiday-title">
        <BsCalendar2WeekFill style={{ marginRight: "5px" }} />
        Public Holidays
      </h1>
      <hr />
      <h1 className="holiday-subtitle">
        <BsFillCalendarPlusFill style={{ marginRight: "5px" }} />
        Edit Payroll
      </h1>
      <hr />

      {showAlert && (
        <Alert variant="success" onClose={() => setShowAlert(false)} dismissible>
          <Alert.Heading>Payroll Updated Successfully!</Alert.Heading>
        </Alert>
      )}

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {payrollData && (
        <div className="edit-holiday-content">
          <div className="holiday-form">
            {/* Header info */}
            <Row className="form-row mb-3">
              <Col md={12}>
                <h5>
                  {payrollData.userName} &nbsp;
                  <Badge bg={statusVariant[payrollData.status] || "secondary"}>
                    {payrollData.status}
                  </Badge>
                </h5>
                <div className="text-muted">
                  User ID: {payrollData.userId} | Department: {payrollData.department} |
                  Period: {payrollData.periodLabel}
                </div>
              </Col>
            </Row>

            {/* Read-only computed values */}
            <Row className="form-row">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Monthly Salary</Form.Label>
                  <Form.Control value={fmt(payrollData.monthlySalary)} readOnly />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Contracted Hours</Form.Label>
                  <Form.Control value={fmt(payrollData.contractedHours)} readOnly />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Hourly Rate</Form.Label>
                  <Form.Control value={fmt(payrollData.hourlyRate, 4)} readOnly />
                </Form.Group>
              </Col>
            </Row>

            <Row className="form-row">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Present Days</Form.Label>
                  <Form.Control value={payrollData.presentDays} readOnly />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Absent Days</Form.Label>
                  <Form.Control value={payrollData.absentDays} readOnly />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Leave Days</Form.Label>
                  <Form.Control value={payrollData.leaveDays} readOnly />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Holiday / WeekOff</Form.Label>
                  <Form.Control
                    value={`${payrollData.holidayDays} / ${payrollData.weekOffDays}`}
                    readOnly
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="form-row">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Worked Hours</Form.Label>
                  <Form.Control value={fmt(payrollData.totalWorkedHours)} readOnly />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Overtime Hours</Form.Label>
                  <Form.Control value={fmt(payrollData.totalOvertimeHours)} readOnly />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Late Minutes</Form.Label>
                  <Form.Control value={payrollData.totalLateMinutes} readOnly />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Early Close Minutes</Form.Label>
                  <Form.Control value={payrollData.totalEarlyCloseMinutes} readOnly />
                </Form.Group>
              </Col>
            </Row>

            <Row className="form-row">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Payable Hours</Form.Label>
                  <Form.Control value={fmt(payrollData.payableHours)} readOnly />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Gross Salary</Form.Label>
                  <Form.Control value={fmt(payrollData.grossSalary)} readOnly />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Period Start</Form.Label>
                  <Form.Control value={fmtDate(payrollData.periodStart)} readOnly />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Period End</Form.Label>
                  <Form.Control value={fmtDate(payrollData.periodEnd)} readOnly />
                </Form.Group>
              </Col>
            </Row>

            <hr />

            {/* Editable adjustments */}
            <h5 className="mb-3">Adjustments</h5>
            <Row className="form-row">
              <Col md={4}>
                <Form.Group controlId="bonus">
                  <Form.Label>Bonus</Form.Label>
                  <Form.Control
                    type="number"
                    name="bonus"
                    value={formData.bonus}
                    onChange={handleChange}
                    min="0"
                    disabled={payrollData.status === "paid"}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="otherDeductions">
                  <Form.Label>Other Deductions</Form.Label>
                  <Form.Control
                    type="number"
                    name="otherDeductions"
                    value={formData.otherDeductions}
                    onChange={handleChange}
                    min="0"
                    disabled={payrollData.status === "paid"}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="deductionNotes">
                  <Form.Label>Deduction Notes</Form.Label>
                  <Form.Control
                    type="text"
                    name="deductionNotes"
                    value={formData.deductionNotes}
                    onChange={handleChange}
                    placeholder="Reason for adjustment..."
                    disabled={payrollData.status === "paid"}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="form-row mt-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Net Salary (auto-calculated)</Form.Label>
                  <Form.Control
                    value={fmt(
                      (payrollData.grossSalary || 0) +
                        (Number(formData.bonus) || 0) -
                        (Number(formData.otherDeductions) || 0)
                    )}
                    readOnly
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="button-content mt-4">
              <Button
                variant="success"
                onClick={handleSubmit}
                disabled={isSaving || payrollData.status === "paid"}
              >
                {isSaving ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" /> Saving...
                  </>
                ) : (
                  "Update Payroll"
                )}
              </Button>

              {payrollData.status === "draft" && (
                <Button
                  variant="warning"
                  style={{ marginLeft: "10px" }}
                  onClick={() => handleStatusChange("finalized")}
                  disabled={isSaving}
                >
                  Finalize
                </Button>
              )}

              {payrollData.status === "finalized" && (
                <Button
                  variant="primary"
                  style={{ marginLeft: "10px" }}
                  onClick={() => handleStatusChange("paid")}
                  disabled={isSaving}
                >
                  Mark as Paid
                </Button>
              )}

              <Button
                variant="secondary"
                onClick={() => navigate("/emp-payroll")}
                style={{ marginLeft: "10px" }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
}